<!-- NOTICE: KAPTION-INTEGRATION -->
<!-- Kaption Copilot Panel - Uses shared Dashboard App SDK composable -->
<script>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import KaptionCopilotAPI from 'dashboard/api/kaptionCopilot';
import { useDashboardAppSDK } from 'dashboard/composables/useDashboardAppSDK';

export default {
  name: 'KaptionCopilotPanel',
  props: {
    conversationId: {
      type: [Number, String],
      default: null,
    },
    currentChat: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: [],
  setup(props) {
    const store = useStore();
    const { t } = useI18n();

    // Refs
    const copilotIframe = ref(null);
    const jwtToken = ref('');
    const isLoading = ref(true);
    const errorMessage = ref('');
    // NOTICE: KAPTION-INTEGRATION - Track if token has been fetched to avoid iframe reloads
    const hasInitializedToken = ref(false);

    // Store getters
    const accountId = computed(() => store.getters.getCurrentAccountId);
    const currentUser = computed(() => store.getters.getCurrentUser);
    const currentAccount = computed(() =>
      store.getters['accounts/getAccount'](accountId.value)
    );
    const globalConfig = computed(() => store.getters['globalConfig/get']);

    // NOTICE: KAPTION-INTEGRATION - URL resolution: account → global → fallback
    const copilotUrl = computed(() => {
      if (currentAccount.value) {
        const accountCopilotUrl = currentAccount.value?.settings?.copilot_url;
        if (accountCopilotUrl) {
          return accountCopilotUrl;
        }
      }

      if (globalConfig.value?.kaptionCopilotUrl) {
        return globalConfig.value.kaptionCopilotUrl;
      }

      return 'https://kaptbase.itzco.app/copilot/kaption-embed';
    });

    const iframeSrc = computed(() => {
      if (!copilotUrl.value || !jwtToken.value) return '';

      const url = new URL(copilotUrl.value);
      url.searchParams.set('token', jwtToken.value);
      return url.toString();
    });

    const contactId = computed(() => props.currentChat?.meta?.sender?.id);

    const currentAgent = computed(() => {
      if (!currentUser.value) return {};
      const { id, name, email } = currentUser.value;
      return { id, name, email };
    });

    const dashboardAppContext = computed(() => {
      const contact = contactId.value
        ? store.getters['contacts/getContact'](contactId.value)
        : null;
      return {
        conversation: props.currentChat,
        contact,
        currentAgent: currentAgent.value,
      };
    });

    // NOTICE: KAPTION-INTEGRATION - Initialize SDK composable
    const sdk = useDashboardAppSDK({
      getIframeElements: () => {
        const iframe = copilotIframe.value;
        return iframe ? [iframe] : [];
      },
      getContext: () => dashboardAppContext.value,
      logPrefix: '[KaptionCopilot]',
      surface: 'sidebar',
      sidebarParams: {},
    });

    // Methods
    const fetchToken = async () => {
      if (!copilotUrl.value) {
        errorMessage.value = 'Copilot URL not configured';
        isLoading.value = false;
        return;
      }

      isLoading.value = true;
      errorMessage.value = '';

      try {
        const response = await KaptionCopilotAPI.fetchToken(
          props.conversationId
        );
        jwtToken.value = response.data.token;
      } catch (error) {
        errorMessage.value = error.message || 'Failed to load Kaption Copilot';
      } finally {
        isLoading.value = false;
      }
    };

    const onIframeLoad = () => {
      sdk.onIframeLoad();
    };

    // NOTICE: KAPTION-INTEGRATION - Handle token refresh request from SDK
    const handleTokenRefresh = async sourceWindow => {
      try {
        const response = await KaptionCopilotAPI.fetchToken(
          props.conversationId
        );
        const newToken = response.data.token;
        const expiresIn = response.data.expires_in;

        // Update our local ref (does NOT change iframeSrc — avoids iframe reload)
        jwtToken.value = newToken;

        // Send the refreshed token to the iframe via postMessage
        sdk.sendTokenRefreshed(sourceWindow, newToken, expiresIn);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[KaptionCopilot] Token refresh failed:', error);
      }
    };

    // NOTICE: KAPTION-INTEGRATION - Send UI config when copilot is ready
    const sendInitialUIConfig = () => {
      const iframe = copilotIframe.value;
      if (iframe) {
        sdk.sendUIConfig(iframe, {
          keyboard: { enabled: true, type: 'text' },
          display: { mode: 'full', scrollable: true },
          capabilities: {
            canSendMessages: true,
            canSendAttachments: true,
            canAccessContacts: true,
          },
        });
      }
    };

    // NOTICE: KAPTION-INTEGRATION - Only fetch token once on first valid conversation
    // Instead of refetching token on every conversation change (which reloads iframe),
    // we keep the iframe alive and send context updates via postMessage
    watch(
      () => props.conversationId,
      newVal => {
        if (newVal && !hasInitializedToken.value) {
          hasInitializedToken.value = true;
          fetchToken();
        }
        // Context updates are handled by the dashboardAppContext watcher below
      },
      { immediate: true }
    );

    // NOTICE: KAPTION-INTEGRATION - Watch for context changes and send updates
    // This handles conversation switches by sending new context via postMessage
    // instead of reloading the iframe with a new token
    watch(
      dashboardAppContext,
      newContext => {
        if (!sdk.iframeLoading.value && hasInitializedToken.value) {
          sdk.sendContextToAllIframes(newContext);
        }
      },
      { deep: true }
    );

    // Message handler reference for cleanup
    let messageHandler = null;

    onMounted(() => {
      messageHandler = sdk.setupMessageListener({
        onLegacyFetchInfo: onIframeLoad,
        onSdkReady: sendInitialUIConfig,
        getAppId: () => 'copilot',
        onTokenRefresh: handleTokenRefresh,
      });
      // NOTICE: KAPTION-INTEGRATION - Token fetching moved to watcher to avoid duplicate calls
      // The watcher handles initial fetch when conversationId is available
    });

    onUnmounted(() => {
      if (messageHandler) {
        sdk.removeMessageListener(messageHandler);
      }
    });

    return {
      // Refs
      copilotIframe,
      jwtToken,
      isLoading,
      errorMessage,

      // Computed
      copilotUrl,
      iframeSrc,

      // Methods
      fetchToken,
      onIframeLoad,

      // i18n
      t,
    };
  },
};
</script>

<template>
  <!-- NOTICE: KAPTION-INTEGRATION -->
  <div class="flex flex-col h-full w-full">
    <div v-if="isLoading" class="flex items-center justify-center h-full">
      <p class="text-n-slate-11">
        {{ t('CONVERSATION.SIDEBAR.COPILOT_LOADING') }}
      </p>
    </div>
    <div
      v-else-if="!copilotUrl"
      class="flex items-center justify-center h-full p-4"
    >
      <p class="text-n-slate-11 text-center text-sm">
        {{ t('CONVERSATION.SIDEBAR.COPILOT_NOT_CONFIGURED') }}
      </p>
    </div>
    <div
      v-else-if="errorMessage"
      class="flex flex-col items-center justify-center h-full p-4 text-red-500"
    >
      <p class="text-n-slate-11 text-center text-sm">{{ errorMessage }}</p>
      <button
        class="mt-2 text-sm text-woot-500 hover:text-woot-600"
        @click="fetchToken"
      >
        {{ t('CONVERSATION.SIDEBAR.COPILOT_RETRY') }}
      </button>
    </div>
    <iframe
      v-else
      ref="copilotIframe"
      :src="iframeSrc"
      class="w-full h-full border-0"
      :title="t('CONVERSATION.SIDEBAR.COPILOT')"
      @load="onIframeLoad"
    />
  </div>
</template>
